import { useEffect, useState, useRef, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import './styles/theme/global.css';

import {
  PageTransition,
  Layout,
  ScrollToTop,
} from './components';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import { universalScrollTo } from './utils/navigationUtils';
// Pages
import HomePage from './pages/home/HomePage';
import OctopusSyncPage from './pages/octopussync/OctopusSyncPage';
import LoginPage from './pages/login/LoginPage';
import ContactsPage from './pages/contacts/ContactsPage';
import NotFoundPage from './pages/NotFoundPage';
import FeatureRequestPage from './components/pages/featureRequest/FeatureRequestPage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';

const AppRoutes = () => {
  const location = useLocation();

  const {
    isPageTransitioning,
    endPageTransition,
    pendingNavigationAction,
    setPendingNavigationAction
  } = useNavigation();

  const [displayLocation, setDisplayLocation] = useState(location);
  const [isAnimating, setIsAnimating] = useState(false);
  const pageSectionRefs = useRef({});
  const [, forceRender] = useState(0);
  const transitionDuration = 1;

  useEffect(() => {
    if (isPageTransitioning) {
      setIsAnimating(true);
    }
  }, [isPageTransitioning]);

  useEffect(() => {
    if (!isAnimating) {
      if (displayLocation.key !== location.key) {
        setDisplayLocation(location);
      }
    }
  }, [location, isAnimating, displayLocation.key]);

  const handleAnimationComplete = useCallback(() => {
    if (pendingNavigationAction) {
      pendingNavigationAction();
      setPendingNavigationAction(null);
    }
    setIsAnimating(false);
    endPageTransition();

    const currentLoc = location;
    if (currentLoc.hash) {
      const id = currentLoc.hash.substring(1);
      setTimeout(() => {
        const element =
          pageSectionRefs.current[id]?.current || document.getElementById(id);
        if (element) {
          const scrollOffset = 100;
          const elementPosition =
            element.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - scrollOffset;
          universalScrollTo(offsetPosition, 0, 'smooth');
        }
      }, 30);
    }
  }, [
    pendingNavigationAction,
    location,
    setPendingNavigationAction,
    endPageTransition,
    pageSectionRefs
  ]);

  const handleSetRefs = useCallback(refsFromPage => {
    pageSectionRefs.current = refsFromPage;
    forceRender(v => v + 1); // trigger re-render so observers can attach to new refs
  }, []);

  const locationForRoutes = isAnimating ? displayLocation : location;

  return (
    <>
      <PageTransition
        isAnimating={isAnimating}
        onAnimationComplete={handleAnimationComplete}
        duration={transitionDuration}
      />

      <Layout pageSectionRefsContainer={pageSectionRefs}>
        <Routes location={locationForRoutes}>
          <Route
            path="/"
            element={<HomePage setRefs={handleSetRefs} />}
          />
          <Route
            path="/octopussync/*"
            element={<OctopusSyncPage setRefs={handleSetRefs} />}
          />
          <Route
            path="/login"
            element={<LoginPage setRefs={handleSetRefs} />}
          />
          <Route
            path="/contacts"
            element={<ContactsPage setRefs={handleSetRefs} />}
          />
          <Route path='/feature-request' element={<FeatureRequestPage />} />
          <Route path='/legal/cookie-policy' element={<CookiePolicyPage />} />
          <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </Layout>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <Router>
      <ScrollToTop />
      <NavigationProvider>
        <AppRoutes />
      </NavigationProvider>
    </Router>
  </HelmetProvider>
);

export default App;
